import { CopyIcon } from '@components'
import cn from '@integrations/classnames'
import toast from '@integrations/notifications'
import { QRCodeSVG } from '@integrations/qr'
import { useState } from '@integrations/react'
import { copyTextToClipboard, isValidString, tr } from 'mmfintech-commons'
import styles from './GoogleAuthenticator.module.scss'

interface Props {
  totpSecretKeyUri?: string
}

function formatSecret(secret: string) {
  return secret.replace(/(.{4})/g, '$1 ').trim()
}

function extractSecret(totpSecretKeyUri: string) {
  return totpSecretKeyUri?.split('?secret=')[1]?.split('&')[0] || ''
}

function GoogleAuthenticator({ totpSecretKeyUri }: Props) {
  const [copied, setCopied] = useState(false)

  if (!isValidString(totpSecretKeyUri)) {
    return null
  }

  const secret = extractSecret(totpSecretKeyUri)

  function handleCopy() {
    if (!secret) return
    copyTextToClipboard(secret)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast.success(tr('FRONTEND.GOOGLE_AUTHENTICATOR.SECRET_COPIED', 'Secret copied to clipboard'))
  }

  return (
    <div className={cn(styles['google-authenticator-wrapper'], 'google-authenticator-wrapper')}>
      <ol className={cn(styles['google-authenticator-steps'], 'google-authenticator-steps')}>
        <li>
          {tr(
            'FRONTEND.GOOGLE_AUTHENTICATOR.STEP_1',
            'Download an authenticator app (e.g. Google Authenticator) on your mobile device.'
          )}
        </li>
        <li>{tr('FRONTEND.GOOGLE_AUTHENTICATOR.STEP_2', 'Scan the QR code below with the app.')}</li>
        <li>{tr('FRONTEND.GOOGLE_AUTHENTICATOR.STEP_3', 'Enter the 6-digit code from the app below.')}</li>
      </ol>

      <div className={cn(styles['google-authenticator-qr'], 'google-authenticator-qr')}>
        <QRCodeSVG value={totpSecretKeyUri} size={150} />
      </div>

      {secret ? (
        <div className={cn(styles['google-authenticator-secret'], 'google-authenticator-secret')}>
          <span className={cn(styles['google-authenticator-secret-label'], 'google-authenticator-secret-label')}>
            {tr('FRONTEND.GOOGLE_AUTHENTICATOR.MANUAL_LABEL', 'or enter this code manually')}
          </span>
          <button
            type='button'
            className={cn(styles['google-authenticator-secret-value'], 'google-authenticator-secret-value')}
            onClick={handleCopy}
            title={tr('FRONTEND.GOOGLE_AUTHENTICATOR.COPY', 'Copy code')}
            data-test='totp-secret-copy'
          >
            <span>{formatSecret(secret)}</span>
            {copied ? (
              <span className={cn(styles['google-authenticator-copied'], 'google-authenticator-copied')}>
                {tr('FRONTEND.GOOGLE_AUTHENTICATOR.COPIED', 'Copied!')}
              </span>
            ) : (
              <CopyIcon />
            )}
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default GoogleAuthenticator
